import { FC } from 'react'

import { useSuspenseQuery } from '@tanstack/react-query'

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Cluster,
  Select,
  Stack,
  Typography,
} from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'
import { getTimeZones } from '@/utils/timezones'

import { currentNotificationPreferencesQueryOptions } from '../api/preferences.service'
import { useNotificationPreferenceUpdate } from '../api/useNotificationPreferenceUpdate'
import { isValidNotificationPreferencesNotifyFor, NOTIFICATION_PREFERENCES_NOTIFY_FOR } from '../domain/settings'

const DEFAULT_SUBSCRIBER_OPTIONS = ['true', 'false'] as const

export const Settings: FC = () => {
  const { translate } = useLocalisation()
  const preferenceMutation = useNotificationPreferenceUpdate()
  const { data: currentNotificationPreferences } = useSuspenseQuery(currentNotificationPreferencesQueryOptions())

  return (
    <>
      <Stack gap={16}>
        <Card>
          <CardHeader>
            <CardTitle>{translate('notification-settings.settings.heading')}</CardTitle>
          </CardHeader>
          <CardContent>
            <Stack gap={16}>
              <Cluster gap={16} x="between">
                <Stack gap={2}>
                  <Typography variant="small">{translate('notification-settings.settings.timezone.title')}</Typography>
                  <Typography variant="tiny" color="muted">
                    {translate('notification-settings.settings.timezone.description')}
                  </Typography>
                </Stack>
                <Select
                  value={currentNotificationPreferences.timezone}
                  onChange={async (newTimeZone) => {
                    await preferenceMutation.mutateAsync({
                      timezone: newTimeZone,
                    })
                  }}
                  items={getTimeZones().map(({ timeZone, label }) => ({
                    value: timeZone,
                    label,
                  }))}
                  searchable
                />
              </Cluster>
              <Cluster gap={16} x="between">
                <Stack gap={2}>
                  <Typography variant="small">{translate('notification-settings.settings.notify.title')}</Typography>
                  <Typography variant="tiny" color="muted">
                    {translate('notification-settings.settings.notify.description')}
                  </Typography>
                </Stack>
                <Select
                  value={currentNotificationPreferences.notifyFor}
                  onChange={async (newNotifyFor) => {
                    if (isValidNotificationPreferencesNotifyFor(newNotifyFor)) {
                      await preferenceMutation.mutateAsync({
                        notifyFor: newNotifyFor,
                      })
                    }
                  }}
                  items={NOTIFICATION_PREFERENCES_NOTIFY_FOR.map((notifyFor) => ({
                    value: notifyFor,
                    label: translate(`notification-settings.settings.notify.options.${notifyFor}`),
                  }))}
                />
              </Cluster>
              <Cluster gap={16} x="between">
                <Stack gap={2}>
                  <Typography variant="small">
                    {translate('notification-settings.settings.default_subscriber.title')}
                  </Typography>
                  <Typography variant="tiny" color="muted">
                    {translate('notification-settings.settings.default_subscriber.description')}
                  </Typography>
                </Stack>
                <Select
                  value={currentNotificationPreferences.defaultSubscriptionLevel === 'all' ? 'true' : 'false'}
                  onChange={async (newIsDefaultSubscriber) => {
                    await preferenceMutation.mutateAsync({
                      defaultSubscriptionLevel: newIsDefaultSubscriber === 'true' ? 'all' : 'none',
                    })
                  }}
                  items={DEFAULT_SUBSCRIBER_OPTIONS.map((value) => ({
                    value,
                    label: translate(`notification-settings.settings.default_subscriber.options.${value}`),
                  }))}
                />
              </Cluster>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </>
  )
}
